import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getAuthToken } from '../../utils/auth/getAuthToken';
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

export type AxiosBaseQueryArgs = {
  url: string;
  method: AxiosRequestConfig['method'];
  /* post body */
  data?: AxiosRequestConfig['data'];
  /* query string params */
  params?: AxiosRequestConfig['params'];
};

export type AxiosBaseQueryConfig = {
  baseUrl: string;
};

/**
 *  A base query function that uses axios to make requests. It automatically
 *  adds the access token to the Authorization header.
 *
 *  @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
 * */
export function axiosBaseQuery(config: AxiosBaseQueryConfig): BaseQueryFn<AxiosBaseQueryArgs> {
  return async function axiosRtkQueryFn({ url, method, data, params }: AxiosBaseQueryArgs, api: BaseQueryApi) {
    try {
      const token = await getAuthToken();
      if (!token) return { error: new Error('Tried to fetch user info without an auth token!') };
      const accessToken = token.access.token;
      const headers = { Authorization: `Bearer ${accessToken}` };

      // RTK will automatically cancel the request if the query is no longer being observed, such as
      // when the component gets unmounted. We can't pass the AbortSignal to the cognito sdk, but we
      // can check manually heare and pass to axios for the real request.
      api.signal.throwIfAborted();

      const result = await axios({ url: config.baseUrl + url, method, data, params, headers, signal: api.signal });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
}
